import React, { useState } from "react";
import {
  Layout,
  Card,
  Form,
  Input,
  Button,
  message,
  Modal,
  Checkbox,
  Typography,
} from "antd";
import { useHistory } from "react-router-dom";
import axios from "axios";
import { BASE_URL, DEFAULT_CREDENTIALS } from "../constants/Constants";
import Swal from "sweetalert2";

const { Content } = Layout;
const { Title, Text } = Typography;

const SignIn = () => {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();

  // Login handler - sends JSON with emailId to match backend LoginRequest
  const onFinish = async (values) => {
    setIsLoading(true);
    try {
      // Create JSON object matching your backend's LoginRequest DTO
      const loginData = {
        emailId: values.email,  // Backend expects emailId field
        password: values.password,
        userType: "ADMIN"
      };

      console.log("Sending login request:", loginData);  // Debug log

      // POST to your backend's /users/loginUser endpoint
      const response = await axios.post(
        `${BASE_URL}/users/loginUser`,
        loginData,
        {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 10000
        }
      );

      console.log("Login response:", response.data);  // Debug log

      // Your backend returns Users object
      const user = response.data;

      if (!user || !user.userId) {
        message.error("Login failed. Please check your credentials.");
        setIsLoading(false);
        return;
      }

      // Store user session data
      localStorage.setItem("userId", user.userId || user.parCode);
      localStorage.setItem("userEmail", user.email || values.email);
      localStorage.setItem("userType", user.userType || "ADMIN");
      localStorage.setItem("isLoggedIn", "true");
      
      // Success notification
      Swal.fire({
        icon: "success",
        title: "Welcome Back!",
        text: `Signed in as ${user.name || 'Administrator'}`,
        timer: 1500,
        showConfirmButton: false,
        toast: true,
        position: "top-end"
      });
      
      // Redirect to dashboard
      history.push("/dashboard");
      
    } catch (error) {
      console.error("Login error:", error);
      
      // Handle different error types
      if (error.response) {
        // Server responded with error status
        if (error.response.status === 401) {
          message.error("Invalid email or password.");
        } else if (error.response.status === 404) {
          message.error("User not found. Please register first.");
        } else if (error.response.status >= 500) {
          message.error("Server error. Please try again later.");
        } else {
          message.error(error.response.data?.message || "Login failed.");
        }
      } else if (error.code === 'ECONNABORTED') {
        message.error("Request timeout. Please check your connection.");
      } else if (error.code === 'ERR_NETWORK') {
        message.error("Cannot connect to server. Is backend running?");
      } else {
        message.error("An unexpected error occurred.");
      }
      
    } finally {
      setIsLoading(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Form validation failed:", errorInfo);
    message.warning("Please fill in all required fields correctly.");
  };

  const showModal = (e) => {
    e.preventDefault();
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields(["forgotEmail"]);
  };

  // Forgot password handler - matches your backend's /forgotPassword endpoint
  const sendEmail = async (values) => {
    setIsLoading(true);
    try {
      console.log("Sending forgot password request for:", values.forgotEmail);
      
      // Your backend's forgotPassword expects email as @RequestParam
      const response = await axios.post(
        `${BASE_URL}/forgotPassword`,
        null,  // empty body
        {
          params: {
            email: values.forgotEmail
          },
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 10000
        }
      );
      
      console.log("Forgot password response:", response.data);
      
      message.success("Password reset instructions sent! Check your inbox.");
      
      Swal.fire({
        icon: "success",
        title: "Reset Email Sent!",
        text: `Instructions sent to ${values.forgotEmail}`,
        timer: 2000,
        showConfirmButton: false,
        toast: true,
        position: "top-end"
      });
      
      handleCancel();
      
    } catch (error) {
      console.error("Forgot password error:", error);
      
      if (error.response) {
        if (error.response.status === 404) {
          message.error("Email not found in our records.");
        } else if (error.response.status === 400) {
          message.error("Invalid email address.");
        } else {
          message.error(error.response.data || "Failed to send reset email.");
        }
      } else {
        message.error("Cannot connect to server. Please try again.");
      }
      
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Layout className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-100">
        <Content className="flex items-center justify-center min-h-screen py-8 px-4">
          <Card
            className="w-full max-w-md shadow-xl border-0"
            title={
              <div className="text-center">
                <Title level={2} className="mb-1 text-gray-800">
                  Admin Portal
                </Title>
                <Text type="secondary" className="text-sm">
                  Sign in to continue to Divine Depot Admin
                </Text>
              </div>
            }
            bordered={false}
            style={{ borderRadius: "12px" }}
          >
            <Form
              form={form}
              name="login-form"
              initialValues={{ 
                email: DEFAULT_CREDENTIALS.ADMIN_EMAIL,  // Pre-fill for testing
                remember: true 
              }}
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              layout="vertical"
              size="large"
              className="login-form"
            >
              {/* Email Input */}
              <Form.Item
                name="email"
                label={
                  <div className="flex items-center">
                    <span className="mr-2 text-gray-500">✉</span>
                    <span className="font-medium text-gray-700">Email Address</span>
                  </div>
                }
                rules={[
                  {
                    required: true,
                    type: "email",
                    message: "Please enter a valid email address",
                  },
                ]}
                className="mb-4"
              >
                <Input
                  placeholder="admin@admin.com"
                  prefix={<span className="text-gray-400 mr-2">📧</span>}
                  disabled={isLoading}
                  className="rounded-lg"
                />
              </Form.Item>

              {/* Password Input */}
              <Form.Item
                name="password"
                label={
                  <div className="flex items-center">
                    <span className="mr-2 text-gray-500">🔒</span>
                    <span className="font-medium text-gray-700">Password</span>
                  </div>
                }
                rules={[
                  {
                    required: true,
                    message: "Please enter your password",
                  },
                ]}
                className="mb-4"
              >
                <Input.Password
                  placeholder="Enter your password"
                  iconRender={(visible) => (
                    visible ? <span>👁</span> : <span>👁‍🗨</span>
                  )}
                  disabled={isLoading}
                  className="rounded-lg"
                />
              </Form.Item>

              {/* Remember Me & Forgot Password */}
              <Form.Item className="mb-4">
                <div className="flex justify-between items-center">
                  <Checkbox 
                    name="remember" 
                    className="text-sm"
                    disabled={isLoading}
                  >
                    <span className="text-gray-600">Remember me</span>
                  </Checkbox>
                  
                  <Button 
                    type="link" 
                    onClick={showModal}
                    size="small"
                    className="p-0 text-blue-600 hover:text-blue-800 text-sm"
                    disabled={isLoading}
                  >
                    Forgot Password?
                  </Button>
                </div>
              </Form.Item>

              {/* Login Button */}
              <Form.Item className="mb-4">
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={isLoading}
                  block
                  size="large"
                  className="h-12 rounded-lg font-medium text-white"
                  style={{
                    background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                    border: "none",
                    boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
                  }}
                >
                  {isLoading ? (
                    <>Signing In...</>
                  ) : (
                    <>Sign In</>
                  )}
                </Button>
              </Form.Item>

              {/* Divider */}
              <Form.Item className="mb-4">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">
                      New to Divine Depot?
                    </span>
                  </div>
                </div>
              </Form.Item>

              {/* Register Button */}
              <Form.Item>
                <Button
                  block
                  size="large"
                  className="h-12 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  onClick={() => history.push("/register")}
                >
                  Create Admin Account
                </Button>
              </Form.Item>
            </Form>

            {/* Development Debug Section */}
            {process.env.NODE_ENV === 'development' && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                <div className="text-xs text-gray-600 mb-2">
                  <strong>🧪 Debug Mode - Test Credentials:</strong>
                </div>
                <div className="text-xs">
                  <div className="mb-1">📧 Email: {DEFAULT_CREDENTIALS.ADMIN_EMAIL}</div>
                  <div className="mb-1">🔑 Password: {DEFAULT_CREDENTIALS.ADMIN_PASSWORD}</div>
                  <div className="mb-2">🎯 Endpoint: localhost:5001/users/loginUser</div>
                  <div className="text-[10px] opacity-75">
                    📦 JSON Sent: {JSON.stringify({
                      emailId: DEFAULT_CREDENTIALS.ADMIN_EMAIL,
                      password: DEFAULT_CREDENTIALS.ADMIN_PASSWORD,
                      userType: "ADMIN"
                    })}
                  </div>
                </div>
              </div>
            )}
          </Card>
        </Content>

        {/* Forgot Password Modal */}
        <Modal
          title={
            <div className="flex items-center">
              <span className="mr-2 text-blue-600">🔑</span>
              <span className="font-medium">Reset Password</span>
            </div>
          }
          visible={isModalVisible}
          onCancel={handleCancel}
          footer={null}
          confirmLoading={isLoading}
          width={450}
          className="forgot-password-modal"
        >
          <Form
            form={form}
            name="forgot-password-form"
            layout="vertical"
            onFinish={sendEmail}
          >
            <Form.Item
              name="forgotEmail"
              label={
                <div className="flex items-center">
                  <span className="mr-2 text-gray-500">✉</span>
                  <span className="font-medium text-gray-700">Email Address</span>
                </div>
              }
              rules={[
                {
                  required: true,
                  type: "email",
                  message: "Please enter a valid email address",
                },
              ]}
              className="mb-4"
            >
              <Input
                placeholder="Enter your admin email address"
                prefix={<span className="text-gray-400 mr-2">📧</span>}
                disabled={isLoading}
                className="rounded-lg"
              />
            </Form.Item>

            <Form.Item className="mb-0">
              <div className="flex gap-3">
                <Button
                  onClick={handleCancel}
                  block
                  size="large"
                  disabled={isLoading}
                  className="flex-1 rounded-lg"
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  size="large"
                  loading={isLoading}
                  className="flex-1 rounded-lg"
                  style={{
                    background: "#1890ff",
                    borderColor: "#1890ff",
                  }}
                >
                  Send Reset Link
                </Button>
              </div>
              
              <div className="mt-3 text-xs text-gray-500 text-center italic">
                We'll send password reset instructions to your email
              </div>
            </Form.Item>
          </Form>
        </Modal>
      </Layout>
    </>
  );
};

export default SignIn;
